import { client } from "@lib/api"
import { RedemptionOption } from "../types/redemption"
import { useCallback, useEffect, useState } from "react"
import { SupportedEWallet } from "./useSupportedEWallet"
import * as Sentry from "@sentry/react"

export type AddEWalletParams = {
    provider: string
    phone_number: string
    label: string
}

export type EditEWalletParams = AddEWalletParams & {
    uuid: string
}

export type EWallet = {
    uuid: string
    owner: string
    provider: string
    phone_number: string
    label: string
    is_deleted: boolean
    is_favorite: boolean
    created_at: string
    updated_at: string
    status: string
    validated_at: string
    iconUrl: string
}

const useEWalletManagement = (fetchEWallets: boolean = true) => {
    const [localLoading, setLocalLoading] = useState(true);

    const addEWallet = async (params: AddEWalletParams) => {
        try {
            return await client.doPost('/v2/wallet/ewallet-account', params)
        } catch (error) {
            Sentry.captureException(error)
            throw error
        }
    }

    const editEWallet = async (params: EditEWalletParams) => {
        try {
            return await client.doPost(`/v2/wallet/ewallet-account/${params.uuid}/update`, {
                provider: params.provider,
                phone_number: params.phone_number,
                label: params.label,
            })
        } catch (error) {
            Sentry.captureException(error)
            throw error
        }
    }

    const removeEWallet = async (ewallet_uuid: string) => {
        try {
            return await client.doDelete(`/v2/wallet/ewallet-account/${ewallet_uuid}`)
        } catch (error) {
            Sentry.captureException(error)
            throw error
        }
    }

    const validateEWallet = async (ewallet_uuid: string) => {
        try {
            return await client.doGet(`/v2/wallet/ewallet-account/${ewallet_uuid}/validation`)
        } catch (error) {
            Sentry.captureException(error)
            throw error
        }
    }

    const [ewallets, setEWallets] = useState<EWallet[]>([])
    const [ewalletAsRedemptionOption, setEWalletAsRedemptionOption] = useState<RedemptionOption[]>([])
    const [supportedEWallets, setSupportedEWallets] = useState<SupportedEWallet[]>([])

    const getDataBase = async () => {
        try {
            setLocalLoading(true)
            const ewallets = await client.doGet('/v2/wallet/ewallet-accounts')
            const supportedEWallets: SupportedEWallet[] = await client.doGet('/v2/wallet/supported-ewallet')
            const mappedEWallets: RedemptionOption[] = ewallets.map((ewallet: EWallet) => {
                return {
                    id: ewallet.uuid,
                    iconUrl: ewallet.iconUrl,
                    accountNumber: ewallet.phone_number,
                    provider: ewallet.provider,
                    name: ewallet.label,
                    isValid: ewallet.status === 'VALIDATED',
                }
            })

            setSupportedEWallets(supportedEWallets)
            setEWalletAsRedemptionOption(mappedEWallets)
            setEWallets(ewallets)
        } catch (error) {
            Sentry.captureException(error)
            throw error
        } finally {
            setLocalLoading(false);
        }
    }

    const getData = useCallback(() => {
        getDataBase()
    }, [])

    useEffect(() => {
        if (!fetchEWallets) return
        getData()
    }, [fetchEWallets])

    const isLoading = localLoading;

    return {
        addEWallet,
        editEWallet,
        removeEWallet,
        validateEWallet,
        ewallets: ewallets as EWallet[],
        ewalletAsRedemptionOption,
        supportedEWallets,
        isLoading,
        reload: getDataBase,
    }
}

export default useEWalletManagement
